import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getComplaintsList,
    deleteComplaint,
    getComplaintEnums,
    getComplaintCategories,
    getComplaintStats,
} from '../../../redux/actions/SocietyPortal/ComplaintAction/ComplaintAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import Pagination from '../../../components/common/Pagination';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_COMPLAINTS_ADD,
    SOCIETY_PORTAL_COMPLAINTS_EDIT,
    SOCIETY_PORTAL_COMPLAINTS_VIEW,
    SOCIETY_PORTAL_COMPLAINT_CATEGORIES,
} from '../../../Routing/routes';

const STATUS_STYLE = {
    PENDING: 'tw-bg-blue-100 tw-text-blue-700',
    IN_PROGRESS: 'tw-bg-amber-100 tw-text-amber-700',
    COMPLETED: 'tw-bg-emerald-100 tw-text-emerald-700',
    REOPENED: 'tw-bg-rose-100 tw-text-rose-700',
};

const PRIORITY_STYLE = {
    LOW: 'tw-bg-slate-100 tw-text-slate-600',
    MEDIUM: 'tw-bg-blue-50 tw-text-blue-600',
    HIGH: 'tw-bg-amber-50 tw-text-amber-700',
    URGENT: 'tw-bg-rose-50 tw-text-rose-700',
};

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—';
const isOverdue = (c) => c.due_date && c.status !== 'COMPLETED' && new Date(c.due_date) < new Date();

// Ageing = time the complaint has been (or was) open.
//   For live complaints  → now - created_at
//   For completed ones   → resolved_at - created_at   (falls back to now if resolved_at is missing)
// Returns a short human label like "2d 5h", "45m", "3w 1d" plus a tone hint
// that the caller uses to tint the badge (heavy red for very old open items,
// grey for completed ones so historical rows don't visually shout).
const computeAge = (c) => {
    if (!c?.created_at) return { label: '—', tone: 'neutral', totalHours: 0 };
    const start = new Date(c.created_at).getTime();
    const isDone = c.status === 'COMPLETED';
    const end = (isDone && c.resolved_at) ? new Date(c.resolved_at).getTime() : Date.now();
    let ms = Math.max(0, end - start);
    const days = Math.floor(ms / 86_400_000);
    ms -= days * 86_400_000;
    const hours = Math.floor(ms / 3_600_000);
    ms -= hours * 3_600_000;
    const mins = Math.floor(ms / 60_000);

    let label;
    if (days >= 7) label = `${Math.floor(days / 7)}w ${days % 7}d`;
    else if (days > 0) label = `${days}d ${hours}h`;
    else if (hours > 0) label = `${hours}h ${mins}m`;
    else label = `${mins}m`;

    let tone = 'neutral';
    if (isDone) tone = 'done';
    else if (isOverdue(c)) tone = 'overdue';
    else {
        const totalHours = (days * 24) + hours;
        if (totalHours >= 72) tone = 'aging';
    }
    return { label, tone };
};

const AGE_TONE_CLASSES = {
    done:    'tw-bg-slate-100 tw-text-slate-500',
    overdue: 'tw-bg-rose-100 tw-text-rose-700',
    aging:   'tw-bg-amber-100 tw-text-amber-700',
    neutral: 'tw-bg-emerald-50 tw-text-emerald-700',
};

// Static color map — Tailwind can't detect dynamic class names built via
// template strings, so we keep every possible class literal here.
const CATEGORY_COLOR_CLASSES = {
    rose:    'tw-bg-rose-100 tw-text-rose-700',
    amber:   'tw-bg-amber-100 tw-text-amber-700',
    emerald: 'tw-bg-emerald-100 tw-text-emerald-700',
    blue:    'tw-bg-blue-100 tw-text-blue-700',
    violet:  'tw-bg-violet-100 tw-text-violet-700',
    cyan:    'tw-bg-cyan-100 tw-text-cyan-700',
    slate:   'tw-bg-slate-200 tw-text-slate-700',
    fuchsia: 'tw-bg-fuchsia-100 tw-text-fuchsia-700',
};

const STAT_CARDS = [
    { key: 'PENDING',      label: 'Pending',     icon: 'mdi:clock-outline',       bg: 'tw-bg-blue-50',    text: 'tw-text-blue-700',    ring: 'tw-border-blue-100' },
    { key: 'IN_PROGRESS',  label: 'In Progress', icon: 'mdi:progress-wrench',     bg: 'tw-bg-amber-50',   text: 'tw-text-amber-700',   ring: 'tw-border-amber-100' },
    { key: 'COMPLETED',    label: 'Completed',   icon: 'mdi:check-circle-outline', bg: 'tw-bg-emerald-50', text: 'tw-text-emerald-700', ring: 'tw-border-emerald-100' },
    { key: 'REOPENED',     label: 'Reopened',    icon: 'mdi:refresh',             bg: 'tw-bg-rose-50',    text: 'tw-text-rose-700',    ring: 'tw-border-rose-100' },
];

const Complaints = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        complaintsListData,
        complaintsListLoading,
        complaintsPagination,
        deleteComplaintLoading,
        complaintEnumsData,
        categoriesData,
        statsData,
        statsLoading,
    } = useSelector(state => state.ComplaintReducer || {});

    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [priority, setPriority] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    const [deleteTarget, setDeleteTarget] = useState(null);
    // Card / list toggle — in-memory only. Table stays the default because
    // this page is dense and admins usually want the columns.
    const [viewMode, setViewMode] = useState('list');

    const fetchList = useCallback(() => {
        if (!societyId) return;
        dispatch(getComplaintsList({
            societyId,
            page,
            limit: itemsPerPage,
            search: search || undefined,
            status: status || undefined,
            category_id: categoryId || undefined,
            priority: priority || undefined,
        }));
    }, [dispatch, societyId, page, search, status, categoryId, priority]);
    usePageRefresh(fetchList);

    useEffect(() => { fetchList(); }, [fetchList]);
    useEffect(() => {
        if (!complaintEnumsData) dispatch(getComplaintEnums());
    }, [dispatch, complaintEnumsData]);
    useEffect(() => {
        if (societyId) dispatch(getComplaintCategories({ societyId }));
    }, [dispatch, societyId]);
    useEffect(() => {
        if (societyId) dispatch(getComplaintStats({ societyId }));
    }, [dispatch, societyId, complaintsListData]);

    const handleDelete = () => {
        if (!deleteTarget) return;
        dispatch(deleteComplaint({
            id: deleteTarget.id,
            onSuccess: () => { setDeleteTarget(null); fetchList(); },
        }));
    };

    const rows = Array.isArray(complaintsListData) ? complaintsListData : [];
    const totalPages = complaintsPagination?.totalPages || 1;

    const statuses = complaintEnumsData?.statuses || [];
    const priorities = complaintEnumsData?.priorities || [];
    const categories = Array.isArray(categoriesData) ? categoriesData : [];

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            {/* Stats banner */}
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3">
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-3 tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-gray-100 tw-text-gray-700 tw-flex tw-items-center tw-justify-center">
                        <Icon icon="mdi:format-list-bulleted" className="tw-text-lg" />
                    </div>
                    <div>
                        <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold">Total</p>
                        <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-leading-tight">
                            {statsLoading ? '—' : (statsData?.total ?? 0)}
                        </p>
                    </div>
                </div>
                {STAT_CARDS.map(card => (
                    <div key={card.key} className={`tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-3 tw-flex tw-items-center tw-gap-3`}>
                        <div className={`tw-w-10 tw-h-10 tw-rounded-lg ${card.bg} ${card.text} tw-flex tw-items-center tw-justify-center`}>
                            <Icon icon={card.icon} className="tw-text-lg" />
                        </div>
                        <div>
                            <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold">{card.label}</p>
                            <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-leading-tight">
                                {statsLoading ? '—' : (statsData?.byStatus?.[card.key] ?? 0)}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Overdue callout */}
            {statsData?.overdue > 0 && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-rose-50 tw-border tw-border-rose-100 tw-text-rose-700 tw-text-xs">
                    <Icon icon="mdi:alert-circle-outline" className="tw-text-base" />
                    <span><strong>{statsData.overdue}</strong> complaint{statsData.overdue === 1 ? ' is' : 's are'} past their due date.</span>
                </div>
            )}

            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Complaints</h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Track and resolve resident complaints</p>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        {/* Card / List view toggle — segmented control */}
                        <div className="tw-inline-flex tw-p-0.5 tw-bg-gray-100 tw-rounded-md">
                            <button
                                type="button"
                                onClick={() => setViewMode('card')}
                                title="Card view"
                                className={`tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-transition-colors ${viewMode === 'card' ? 'tw-bg-white tw-text-gray-800 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                            >
                                <Icon icon="mdi:view-grid-outline" className="tw-text-base" />
                                Cards
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode('list')}
                                title="List view"
                                className={`tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-transition-colors ${viewMode === 'list' ? 'tw-bg-white tw-text-gray-800 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                            >
                                <Icon icon="mdi:format-list-bulleted" className="tw-text-base" />
                                List
                            </button>
                        </div>
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINT_CATEGORIES)}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50"
                        >
                            <Icon icon="mdi:tag-multiple-outline" className="tw-text-base" />
                            Categories
                        </button>
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS_ADD)}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-transition-colors"
                        >
                            <Icon icon="mdi:plus" className="tw-text-base" />
                            New Complaint
                        </button>
                    </div>
                </div>

                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-bg-gray-50/50 tw-grid tw-grid-cols-1 sm:tw-grid-cols-5 tw-gap-3">
                    <div className="sm:tw-col-span-2 tw-relative">
                        <Icon icon="mdi:magnify" className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by code, subject, description..."
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1); }}
                            className="tw-w-full tw-pl-9 tw-pr-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                        />
                    </div>
                    <select
                        value={status}
                        onChange={e => { setStatus(e.target.value); setPage(1); }}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                    >
                        <option value="">All Status</option>
                        {statuses.map(s => <option key={s} value={s}>{humanize(s)}</option>)}
                    </select>
                    <select
                        value={categoryId}
                        onChange={e => { setCategoryId(e.target.value); setPage(1); }}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                    >
                        <option value="">All Categories</option>
                        {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                    </select>
                    <select
                        value={priority}
                        onChange={e => { setPriority(e.target.value); setPage(1); }}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                    >
                        <option value="">All Priorities</option>
                        {priorities.map(p => <option key={p} value={p}>{humanize(p)}</option>)}
                    </select>
                </div>

                {viewMode === 'list' ? (
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Code</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Subject</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Category</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Priority</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Status</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Due</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]" title="Time since raised. For completed complaints, time between raise and resolution.">
                                    Ageing
                                </th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Assigned To</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {complaintsListLoading && (
                                <tr>
                                    <td colSpan={9} className="tw-text-center tw-py-12">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                                            <span className="tw-text-xs tw-text-gray-400">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!complaintsListLoading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">
                                        No complaints found
                                    </td>
                                </tr>
                            )}
                            {!complaintsListLoading && rows.map((c) => {
                                const id = c.complaint_id;
                                const assignedTo = c.assignedTo
                                    ? [c.assignedTo.first_name, c.assignedTo.last_name].filter(Boolean).join(' ').trim() || c.assignedTo.email
                                    : '—';
                                const categoryName = c.category?.name || '—';
                                const catColor = c.category?.color || 'slate';
                                const catIcon = c.category?.icon || 'mdi:tag-outline';
                                return (
                                    <tr key={id} className="tw-border-b tw-border-gray-50 hover:tw-bg-slate-50/50 tw-transition-colors">
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-800 tw-font-mono tw-font-semibold">{c.complaint_code}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-800 tw-max-w-xs tw-truncate" title={c.subject}>{c.subject}</td>
                                        <td className="tw-px-4 tw-py-2.5">
                                            <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${CATEGORY_COLOR_CLASSES[catColor] || CATEGORY_COLOR_CLASSES.slate}`}>
                                                <Icon icon={catIcon} className="tw-text-xs" /> {categoryName}
                                            </span>
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5">
                                            <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${PRIORITY_STYLE[c.priority] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                {humanize(c.priority)}
                                            </span>
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5">
                                            <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${STATUS_STYLE[c.status] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                {humanize(c.status)}
                                            </span>
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5">
                                            {c.due_date ? (
                                                <span className={`tw-inline-flex tw-items-center tw-gap-1 ${isOverdue(c) ? 'tw-text-rose-700 tw-font-semibold' : 'tw-text-gray-700'}`}>
                                                    {isOverdue(c) && <Icon icon="mdi:alert-circle" className="tw-text-sm" />}
                                                    {fmtDate(c.due_date)}
                                                </span>
                                            ) : <span className="tw-text-gray-700">—</span>}
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5">
                                            {(() => {
                                                const age = computeAge(c);
                                                return (
                                                    <span
                                                        title={c.status === 'COMPLETED' ? `Resolved ${age.label} after being raised` : `Open for ${age.label}`}
                                                        className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${AGE_TONE_CLASSES[age.tone]}`}
                                                    >
                                                        <Icon icon={age.tone === 'overdue' ? 'mdi:timer-alert-outline' : 'mdi:clock-outline'} className="tw-text-xs" />
                                                        {age.label}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">{assignedTo}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-right">
                                            <div className="tw-inline-flex tw-items-center tw-gap-1">
                                                <button
                                                    title="View"
                                                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS_VIEW.replace(':id', id))}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-blue-600"
                                                >
                                                    <Icon icon="mdi:eye-outline" className="tw-text-base" />
                                                </button>
                                                <button
                                                    title="Edit"
                                                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS_EDIT.replace(':id', id))}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                                                >
                                                    <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                                                </button>
                                                <button
                                                    title="Delete"
                                                    onClick={() => setDeleteTarget({ id, code: c.complaint_code, subject: c.subject })}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600"
                                                >
                                                    <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                ) : (
                    // Card view — same data, denser at-a-glance layout.
                    <div className="tw-p-4">
                        {complaintsListLoading && (
                            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-12">
                                <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                                <span className="tw-text-xs tw-text-gray-400">Loading...</span>
                            </div>
                        )}
                        {!complaintsListLoading && rows.length === 0 && (
                            <div className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">
                                No complaints found
                            </div>
                        )}
                        {!complaintsListLoading && rows.length > 0 && (
                            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3">
                                {rows.map((c) => {
                                    const id = c.complaint_id;
                                    const assignedTo = c.assignedTo
                                        ? [c.assignedTo.first_name, c.assignedTo.last_name].filter(Boolean).join(' ').trim() || c.assignedTo.email
                                        : '—';
                                    const categoryName = c.category?.name || '—';
                                    const catColor = c.category?.color || 'slate';
                                    const catIcon = c.category?.icon || 'mdi:tag-outline';
                                    const age = computeAge(c);
                                    return (
                                        <div
                                            key={id}
                                            className="tw-relative tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 hover:tw-shadow-md hover:tw-border-rose-200 tw-transition-all tw-flex tw-flex-col tw-gap-2"
                                        >
                                            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                                <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-700 tw-font-mono tw-font-semibold tw-text-[10px]">
                                                    {c.complaint_code}
                                                </span>
                                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${STATUS_STYLE[c.status] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                    {humanize(c.status)}
                                                </span>
                                            </div>
                                            <div className="tw-text-xs tw-font-semibold tw-text-gray-800 tw-line-clamp-2" title={c.subject}>
                                                {c.subject}
                                            </div>
                                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                                                <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${CATEGORY_COLOR_CLASSES[catColor] || CATEGORY_COLOR_CLASSES.slate}`}>
                                                    <Icon icon={catIcon} className="tw-text-xs" /> {categoryName}
                                                </span>
                                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${PRIORITY_STYLE[c.priority] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                    {humanize(c.priority)}
                                                </span>
                                                <span
                                                    title={c.status === 'COMPLETED' ? `Resolved ${age.label} after being raised` : `Open for ${age.label}`}
                                                    className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${AGE_TONE_CLASSES[age.tone]}`}
                                                >
                                                    <Icon icon={age.tone === 'overdue' ? 'mdi:timer-alert-outline' : 'mdi:clock-outline'} className="tw-text-xs" />
                                                    {age.label}
                                                </span>
                                            </div>
                                            <div className="tw-flex tw-items-center tw-justify-between tw-text-[11px] tw-text-gray-600 tw-pt-1 tw-border-t tw-border-gray-100 tw-mt-auto">
                                                <span className="tw-inline-flex tw-items-center tw-gap-1" title="Due date">
                                                    <Icon icon="mdi:calendar-clock-outline" className="tw-text-sm tw-text-gray-400" />
                                                    {c.due_date ? (
                                                        <span className={isOverdue(c) ? 'tw-text-rose-700 tw-font-semibold' : ''}>
                                                            {fmtDate(c.due_date)}
                                                        </span>
                                                    ) : <span>—</span>}
                                                </span>
                                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-truncate tw-max-w-[50%]" title={`Assigned to ${assignedTo}`}>
                                                    <Icon icon="mdi:account-circle-outline" className="tw-text-sm tw-text-gray-400" />
                                                    <span className="tw-truncate">{assignedTo}</span>
                                                </span>
                                            </div>
                                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-1 tw-pt-1">
                                                <button
                                                    title="View"
                                                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS_VIEW.replace(':id', id))}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-blue-600"
                                                >
                                                    <Icon icon="mdi:eye-outline" className="tw-text-base" />
                                                </button>
                                                <button
                                                    title="Edit"
                                                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS_EDIT.replace(':id', id))}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                                                >
                                                    <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                                                </button>
                                                <button
                                                    title="Delete"
                                                    onClick={() => setDeleteTarget({ id, code: c.complaint_code, subject: c.subject })}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600"
                                                >
                                                    <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {!complaintsListLoading && rows.length > 0 && totalPages > 1 && (
                    <div className="tw-p-4 tw-border-t tw-border-gray-100">
                        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                    </div>
                )}
            </div>

            {deleteTarget && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-sm tw-overflow-hidden">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">Delete Complaint?</h3>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">This action cannot be undone.</p>
                        </div>
                        <div className="tw-p-5">
                            <p className="tw-text-xs tw-text-gray-600 tw-mb-4">
                                You are about to delete <span className="tw-font-semibold">{deleteTarget.code}</span> — {deleteTarget.subject}.
                            </p>
                            <div className="tw-flex tw-justify-end tw-gap-2">
                                <button onClick={() => setDeleteTarget(null)} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                                <button onClick={handleDelete} disabled={deleteComplaintLoading} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50">
                                    {deleteComplaintLoading ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Complaints;
